import { Suspense, useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from './Icon';
import { faBars, faCartShopping, faMagnifyingGlass, faXmark, faPhone, faEnvelope, faLocationDot, faArrowRight, faUser, faGlobe } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { useAccount, useCart, useSettings } from '../lib/store';
import { categoryHref } from '../lib/format';
import { LanguageSwitch, useI18n } from '../lib/i18n';
import { pageHref, usePageList } from '../lib/pages';
import InstallPrompt from './InstallPrompt';
import { imgUrl, responsive } from '../lib/img';

const NAV = [
  { key: 'home', to: '/', label: 'Home' },
  { key: 'chargers', to: '/shop?category=chargers', label: 'EV Chargers' },
  { key: 'installation', to: '/installation', label: 'Installation' },
  { key: 'adapters', to: '/shop?category=adapters', label: 'Adapters' },
  { key: 'cables', to: '/shop?category=cables', label: 'Cables' },
  { key: 'shop', to: '/shop', label: 'All Products' },
  { key: 'track', to: '/track', label: 'Track Order' },
  { key: 'about', to: '/about', label: 'About Us' },
];

function activeKey({ pathname, search }) {
  if (pathname === '/') return 'home';
  if (pathname === '/shop') return new URLSearchParams(search).get('category') || 'shop';
  return pathname.split('/')[1];
}

function Header() {
  const { settings: s } = useSettings();
  const { count } = useCart();
  const { user } = useAccount();
  const { t, pick } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const active = activeKey(location);
  useEffect(() => setOpen(false), [location.pathname, location.search]);

  const onSearch = (e) => {
    e.preventDefault();
    const q = new FormData(e.currentTarget).get('q').trim();
    navigate(q ? `/shop?q=${encodeURIComponent(q)}` : '/shop');
  };

  return (
    <>
      {s?.announcement && <div className="bg-navy px-4 py-2 text-center text-[13px] text-brand-100">{pick(s, 'announcement')}</div>}
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/90 backdrop-blur-md supports-[backdrop-filter]:bg-white/75">
        <div className="shell flex h-[72px] items-center gap-3 sm:gap-5">
          <Link to="/" className="shrink-0"><img {...responsive(s?.logo || '/uploads/tatheer-logo.png', '(min-width: 640px) 84px, 69px', 240)} alt={(s && pick(s, 'storeName')) || t('Tatheer')} width="191" height="100" className="h-8 w-auto min-[360px]:h-9 sm:h-11" /></Link>
          <nav className={`${open ? 'flex' : 'hidden'} absolute inset-x-0 top-[72px] flex-col gap-1 border-b border-slate-200 bg-white p-4 shadow-card xl:static xl:flex xl:flex-1 xl:flex-row xl:gap-0.5 xl:border-0 xl:bg-transparent xl:p-0 xl:shadow-none`}>
            <form onSubmit={onSearch} className="mb-2 flex h-11 items-center rounded-full border border-slate-200 bg-soft pe-1.5 ps-4 focus-within:border-brand-500 xl:hidden">
              <input name="q" placeholder={t('Search chargers, cables…')} aria-label={t('Search')} className="min-w-0 flex-1 bg-transparent text-sm outline-none" />
              <button className="grid size-8 cursor-pointer place-items-center text-slate-500 hover:text-brand-600" aria-label={t('Search')}><FontAwesomeIcon icon={faMagnifyingGlass} /></button>
            </form>
            {NAV.map((n) => (
              <Link key={n.key} to={n.to}
                className={`rounded-lg px-3 py-2 text-sm font-semibold whitespace-nowrap transition xl:px-2.5 ${active === n.key ? 'bg-soft text-brand-600' : 'text-slate-600 hover:bg-soft hover:text-brand-600'}`}>
                {t(n.label)}
              </Link>
            ))}
          </nav>
          <div className="ms-auto flex items-center gap-1.5 sm:gap-2.5">
            <form onSubmit={onSearch} className="hidden h-10 items-center rounded-full border border-slate-200 bg-soft pe-1.5 ps-4 transition focus-within:border-brand-500 2xl:flex">
              <input name="q" placeholder={t('Search chargers, cables…')} aria-label={t('Search')} className="w-44 bg-transparent text-sm outline-none" />
              <button className="grid size-8 cursor-pointer place-items-center text-slate-500 hover:text-brand-600" aria-label={t('Search')}><FontAwesomeIcon icon={faMagnifyingGlass} /></button>
            </form>
            <Link to="/shop" aria-label={t('Search products')} title={t('Search products')}
              className="hidden size-11 place-items-center rounded-full border border-slate-200 bg-white transition hover:border-brand-500 hover:text-brand-600 xl:grid 2xl:hidden">
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </Link>
            <span className="hidden sm:contents"><LanguageSwitch /></span>
            <span className="contents sm:hidden"><LanguageSwitch compact /></span>
            <Link to={user ? '/account' : '/login'} aria-label={user ? t('My account') : t('Log in')} title={user ? t('My account') : t('Log in / Create account')}
              className={`flex h-10 items-center gap-2 rounded-full border px-3 text-sm sm:h-11 sm:px-3.5 font-semibold transition hover:border-brand-500 hover:text-brand-600 ${active === 'account' || active === 'login' ? 'border-brand-500 text-brand-600' : 'border-slate-200 bg-white'}`}>
              <FontAwesomeIcon icon={faUser} />
              <span className="hidden max-w-28 truncate sm:inline">{user ? user.name.split(' ')[0] : t('Log in')}</span>
            </Link>
            <Link to="/cart" aria-label={t('Cart')} className="relative grid size-10 place-items-center sm:size-11 rounded-full border border-slate-200 bg-white transition hover:border-brand-500 hover:text-brand-600">
              <FontAwesomeIcon icon={faCartShopping} />
              {count > 0 && <span className="absolute -top-1 -end-1 grid h-5 min-w-5 place-items-center rounded-full bg-sale px-1 text-[11px] font-bold text-white">{count}</span>}
            </Link>
            <button onClick={() => setOpen((o) => !o)} aria-label={t('Menu')} aria-expanded={open}
              className="grid size-10 cursor-pointer place-items-center rounded-full border border-slate-200 bg-white sm:size-11 xl:hidden">
              <FontAwesomeIcon icon={open ? faXmark : faBars} />
            </button>
          </div>
        </div>
      </header>
    </>
  );
}

function Footer() {
  const { settings: s } = useSettings();
  const pages = usePageList();
  const { t, pick } = useI18n();
  if (!s) return null;
  const col = 'mb-3.5 text-[15px] font-bold text-white';
  const link = 'block py-1 transition hover:text-white';
  const groupName = pick(s, 'groupName');
  return (
    <footer className="bg-navy pt-12 pb-24 text-sm text-slate-400 sm:pt-14 sm:pb-6">
      <div className="shell">
        <div className="grid grid-cols-2 gap-x-5 gap-y-8 lg:grid-cols-[1.4fr_1fr_1fr_1fr_1.2fr]">
          <div className="col-span-2 lg:col-span-1">
            <Link to="/" className="mb-4 inline-block"><img {...responsive(s.logo, '77px', 240)} alt={pick(s, 'storeName')} width="191" height="100" loading="lazy" className="h-10 w-auto" /></Link>
            <p>{t('{tagline}. EV chargers, accessories and certified installation across the UAE and GCC.', { tagline: pick(s, 'tagline') })}</p>
            {s.groupName && (
              <div className="mt-5 flex items-center gap-3.5 rounded-xl border border-white/10 bg-white/5 p-3.5">
                {s.groupLogo && (s.groupWebsite ? (
                  <a href={/^https?:\/\//.test(s.groupWebsite) ? s.groupWebsite : `https://${s.groupWebsite}`} target="_blank" rel="noopener" className="shrink-0" aria-label={groupName}>
                    <img src={imgUrl(s.groupLogo, 240)} alt={groupName} loading="lazy" width="96" height="96" className="size-20 object-contain transition hover:scale-105 sm:size-24" />
                  </a>
                ) : <img src={imgUrl(s.groupLogo, 240)} alt={groupName} loading="lazy" width="96" height="96" className="size-20 shrink-0 object-contain sm:size-24" />)}
                <div className="min-w-0">
                <p className="text-xs tracking-wide text-slate-400 uppercase">{t('A member of')}</p>
                <p className="font-bold text-white">{groupName}</p>
                {s.groupWebsite && (
                  <a href={/^https?:\/\//.test(s.groupWebsite) ? s.groupWebsite : `https://${s.groupWebsite}`} target="_blank" rel="noopener"
                    className="mt-1 block py-1.5 transition hover:text-white"><FontAwesomeIcon icon={faGlobe} className="me-2 w-4 text-accent" /><span dir="ltr">{s.groupWebsite.replace(/^https?:\/\//, '').replace(/\/$/, '')}</span></a>
                )}
                {s.groupEmail && <a href={`mailto:${s.groupEmail}`} className="block truncate py-1.5 transition hover:text-white"><FontAwesomeIcon icon={faEnvelope} className="me-2 w-4 text-accent" /><span dir="ltr">{s.groupEmail}</span></a>}
                </div>
              </div>
            )}
          </div>
          <div>
            <h2 className={col}>{t('Shop')}</h2>
            {s.categories.map((c) => <Link key={c.key} to={categoryHref(c.key)} className={link}>{pick(c, 'name')}</Link>)}
          </div>
          <div>
            <h2 className={col}>{t('Chargers')}</h2>
            <Link to="/shop?category=chargers&type=AC" className={link}>{t('AC Chargers')}</Link>
            <Link to="/shop?category=chargers&type=DC" className={link}>{t('DC Fast Chargers')}</Link>
            <Link to="/shop?category=chargers&usage=private" className={link}>{t('Home / Private')}</Link>
            <Link to="/shop?category=chargers&usage=public" className={link}>{t('Commercial / Public')}</Link>
          </div>
          <div>
            <h2 className={col}>{t('Information')}</h2>
            <Link to="/about" className={link}>{t('About Us')}</Link>
            {pages.filter((p) => p.group === 'policy').map((p) => <Link key={p.slug} to={pageHref(p.slug)} className={link}>{pick(p, 'title')}</Link>)}
            <Link to="/track" className={link}>{t('Track Order')}</Link>
          </div>
          <div className="space-y-1">
            <h2 className={col}>{t('Contact')}</h2>
            <a href={`tel:${s.phone.replace(/\s/g, '')}`} className={link}><FontAwesomeIcon icon={faPhone} className="me-2 w-4 text-accent" /><span dir="ltr">{s.phone}</span></a>
            <a href={`mailto:${s.email}`} className={`${link} truncate`}><FontAwesomeIcon icon={faEnvelope} className="me-2 w-4 text-accent" /><span dir="ltr">{s.email}</span></a>
            <p className="py-1"><FontAwesomeIcon icon={faLocationDot} className="me-2 w-4 text-accent" />{pick(s, 'address')}</p>
            <Link to="/installation#quote" className={`${link} font-semibold text-brand-300`}>{t('Request a site survey')} <FontAwesomeIcon icon={faArrowRight} className="ms-1" /></Link>
          </div>
        </div>
        <div className="mt-9 flex flex-col items-center gap-2 border-t border-white/10 pt-5 text-center text-[13px] sm:flex-row sm:flex-wrap sm:justify-between sm:gap-3 sm:text-start">
          <span>© {new Date().getFullYear()} {s.groupName ? t('{store}, part of {group}. All rights reserved.', { store: pick(s, 'storeName'), group: groupName }) : t('{store}. All rights reserved.', { store: pick(s, 'storeName') })} · <Link to="/policies/privacy-policy" className="hover:text-white">{t('Privacy')}</Link> · <Link to="/policies/terms" className="hover:text-white">{t('Terms')}</Link></span>
          <span>{s.vatPercent ? t('Prices in {currency}, excluding {vat}% VAT.', { currency: t(s.currency), vat: s.vatPercent }) : t('Prices in {currency}.', { currency: t(s.currency) })}</span>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  const { settings, error } = useSettings();
  const { t } = useI18n();
  if (error) {
    return <div className="shell py-20 text-center text-slate-500">{t('The store is temporarily unavailable ({error}). Please try again shortly.', { error })}</div>;
  }
  return (
    <div className="flex min-h-dvh flex-col">
      <Header />
      <main className="flex-1">{settings ? <Suspense fallback={<div className="shell py-10"><div className="skeleton h-80" /></div>}><Outlet /></Suspense> : <div className="shell py-10"><div className="skeleton h-80" /></div>}</main>
      <Footer />
      <InstallPrompt />
      {settings?.whatsapp && (
        <a href={`https://wa.me/${settings.whatsapp}`} target="_blank" rel="noopener noreferrer" aria-label={t('Chat on WhatsApp')}
          className="fixed end-5 bottom-[max(1.25rem,env(safe-area-inset-bottom))] z-40 grid size-14 place-items-center rounded-full bg-[#25d366] text-3xl text-white shadow-lg transition hover:scale-105">
          <FontAwesomeIcon icon={faWhatsapp} />
        </a>
      )}
    </div>
  );
}
